import { PDFDocument, PDFImage, PDFName, PDFPage, PDFString, rgb } from "pdf-lib"
import { isPng } from "@services/pdf/assetLoader"
import { pageHeight, pageWidth } from "@services/pdf/constants"

/** Size helpers — ports of iOS CGSize.aspectFitSize / aspectFillSize. */
export interface ISize {
	width: number
	height: number
}

export function aspectFitSize(source: ISize, bounds: ISize): ISize {
	const scale = Math.min(bounds.width / source.width, bounds.height / source.height)
	return { width: source.width * scale, height: source.height * scale }
}

export function aspectFillSize(source: ISize, bounds: ISize): ISize {
	const scale = Math.max(bounds.width / source.width, bounds.height / source.height)
	return { width: source.width * scale, height: source.height * scale }
}

/** White page fill (iOS rasterization always paints a white background). */
export function drawWhiteBackground(page: PDFPage): void {
	page.drawRectangle({
		x: 0,
		y: 0,
		width: pageWidth,
		height: pageHeight,
		color: rgb(1, 1, 1),
	})
}

/**
 * Port of iOS drawOrgTemplate: the template PDF's FIRST page is drawn
 * stretched to fill the full A4 page (iOS rasterizes then draws in the
 * full-page rect, which stretches both axes — replicated with a vector embed).
 */
export async function drawTemplateBackground(
	doc: PDFDocument,
	page: PDFPage,
	templateBytes: Uint8Array | null
): Promise<void> {
	if (!templateBytes) {
		return
	}
	try {
		const [embedded] = await doc.embedPdf(new Uint8Array(templateBytes), [0])
		page.drawPage(embedded, {
			x: 0,
			y: 0,
			xScale: pageWidth / embedded.width,
			yScale: pageHeight / embedded.height,
		})
	} catch {
		// unusable template — parity with iOS silently skipping a bad download
	}
}

export async function embedImage(doc: PDFDocument, bytes: Uint8Array): Promise<PDFImage> {
	if (isPng(bytes)) {
		return doc.embedPng(new Uint8Array(bytes))
	}
	return doc.embedJpg(new Uint8Array(bytes))
}

/**
 * Draw an image into a top-down rect (iOS UIImage.draw(in:)).
 */
export function drawImageInTopRect(
	page: PDFPage,
	image: PDFImage,
	x: number,
	yTop: number,
	width: number,
	height: number
): void {
	page.drawImage(image, {
		x,
		y: pageHeight - yTop - height,
		width,
		height,
	})
}

/**
 * Link annotation (iOS PDFAnnotation link with PDFActionURL).
 * Rect is given in PDF coordinates (bottom-left origin) exactly as iOS passes it.
 */
export function addLinkAnnotation(
	doc: PDFDocument,
	page: PDFPage,
	rect: { x: number; y: number; width: number; height: number },
	url: string
): void {
	const link = doc.context.register(
		doc.context.obj({
			Type: "Annot",
			Subtype: "Link",
			Rect: [rect.x, rect.y, rect.x + rect.width, rect.y + rect.height],
			Border: [0, 0, 0],
			A: {
				Type: "Action",
				S: "URI",
				URI: PDFString.of(url),
			},
		})
	)
	page.node.set(PDFName.of("Annots"), doc.context.obj([link]))
}
